import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Alert } from '@/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import {
  useRorosDomains,
  useRorosDomainActivate,
  useRorosDomainDeactivate,
} from '@/hooks/roros/domains';
import { useRorosActors } from '@/hooks/roros/actors';
import { usePermissions } from '@/hooks/usePermissions';
import { DOMAIN_LIST } from '@/routes/roros/launchpad/types';
import { DomainSettingsDialog } from './DomainSettingsDialog';
import { Globe, Settings, Power, PowerOff } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { t } from '@/lib/i18n';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';

function getDomainIcon(domainId: number) {
  const domainInfo = DOMAIN_LIST.find((d) => d.domain_id === domainId);
  if (!domainInfo) return <Globe className="h-5 w-5" />;
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[domainInfo.icon];
  return IconComponent ? <IconComponent className="h-5 w-5" /> : <Globe className="h-5 w-5" />;
}

/**
 * DomainsPanel — content only, used in the Structure Settings "Domains" tab.
 * The DomainsPage export below wraps this in RorosPage for the legacy
 * /roros/domains route.
 *
 * AC-Domains-1 (PW6): Inline AccountablePicker removed — replaced with
 * read-only display. View button removed. Gap added between pills and headline.
 * AC-Domains-3 (PW6): domain.description (API) used directly as the
 * description source of truth — hard-coded helper deleted.
 */
export function DomainsPanel() {
  const domainsQuery = useRorosDomains();
  const actorsQuery = useRorosActors();
  const activateMutation = useRorosDomainActivate();
  const deactivateMutation = useRorosDomainDeactivate();
  const { hasPermission } = usePermissions();
  const canManageDomains = hasPermission('roros.domains.manage');

  const [settingsDomain, setSettingsDomain] = useState<RorosDomainDTO | null>(null);
  const [deactivateDomain, setDeactivateDomain] = useState<RorosDomainDTO | null>(null);

  const actors = useMemo<RorosActorDTO[]>(() => {
    const raw = actorsQuery.data as RorosActorDTO[] | undefined;
    return raw ?? [];
  }, [actorsQuery.data]);

  const handleActivate = (domain: RorosDomainDTO) => {
    activateMutation.mutate(domain.id, {
      onSuccess: () => showToast.success(t('Domain activated')),
      onError: (err) => showToast.error(err instanceof Error ? err.message : t('Failed to activate domain')),
    });
  };

  const handleDeactivateConfirm = () => {
    if (!deactivateDomain) return;
    deactivateMutation.mutate(deactivateDomain.id, {
      onSuccess: () => {
        showToast.success(t('Domain deactivated'));
        setDeactivateDomain(null);
      },
      onError: (err) => {
        showToast.error(err instanceof Error ? err.message : t('Failed to deactivate domain'));
        setDeactivateDomain(null);
      },
    });
  };

  return (
    <div>
      {/* TB-4 (AC-SSV-04): Read-only hint for non-managers. */}
      {!canManageDomains && (
        <Alert variant="info" title={t('Read-only view')} data-testid="domains-readonly-hint" className="mb-4">
          {t('You can view domain configuration, but you need the Domains Manage permission to activate, deactivate, or edit domains.')}
        </Alert>
      )}
      <QueryPage query={domainsQuery} emptyMessage={t('No domains found')}>
        {(domains) => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {domains.map((domain) => {
              const isStrategy = domain.domainId === 1;
              const isActive = domain.isActive;

              // Dim the informational content (icon, title, description,
              // counts) for inactive domains to signal "off", but keep the
              // action row fully opaque — otherwise the Activate button
              // looked disabled and users couldn't tell it was the control
              // that would turn the domain back on.
              const dimClass = !isActive ? 'opacity-60' : '';

              // AC-Domains-1: look up actor name for read-only accountable display.
              const accountableActor = domain.accountableActorId
                ? actors.find((a) => a.id === domain.accountableActorId)
                : null;
              const accountableLabel = accountableActor ? accountableActor.name : '—';

              return (
                <Card
                  key={domain.id}
                  className={!isActive ? 'border-dashed' : undefined}
                >
                  <CardHeader className="pb-2">
                    {/* gap-4 between name area and status pill (AC-Domains-1) */}
                    <div className="flex items-start justify-between gap-4">
                      <Link
                        to={`/roros/domains/${domain.id}`}
                        className={`flex items-center gap-2 hover:opacity-80 transition-opacity ${dimClass}`}
                      >
                        <div className={`p-2 rounded-lg ${isActive ? 'bg-primary-100 text-primary-600' : 'bg-muted text-muted-foreground'}`}>
                          {getDomainIcon(domain.domainId)}
                        </div>
                        <div>
                          <CardTitle className="text-base">{domain.name}</CardTitle>
                          {domain.settings?.isAdvisory && (
                            <Badge variant="secondary" className="mt-1">{t('Advisory')}</Badge>
                          )}
                        </div>
                      </Link>
                      {isStrategy ? (
                        <Badge variant="success">{t('Always Active')}</Badge>
                      ) : (
                        <Badge variant={isActive ? 'success' : 'secondary'}>
                          {isActive ? t('Active') : t('Inactive')}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* AC-Domains-3: use domain.description from API (not a hard-coded helper) */}
                    <p className={`text-sm text-muted-foreground mb-4 ${dimClass}`}>
                      {domain.description}
                    </p>

                    <div className={`flex items-center gap-4 text-sm text-muted-foreground mb-4 ${dimClass}`}>
                      <span>{domain.actorCount} {domain.actorCount !== 1 ? t('actors') : t('actor')}</span>
                      <span>{domain.workItemCount} {domain.workItemCount !== 1 ? t('work items') : t('work item')}</span>
                    </div>

                    {/* AC-Domains-1: read-only accountable display (no inline picker) */}
                    <div
                      data-testid={`domain-accountable-display-${domain.id}`}
                      className={`text-xs text-muted-foreground mb-3 ${dimClass}`}
                    >
                      {t('Accountable')}: {accountableLabel}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* TB-4 (AC-SSV-04): Settings button only for managers (dialog contains edit form). */}
                      {canManageDomains && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSettingsDomain(domain)}
                          data-testid={`domain-settings-btn-${domain.id}`}
                        >
                          <Settings className="h-3.5 w-3.5 mr-1" />
                          {t('Settings')}
                        </Button>
                      )}

                      {!isStrategy && canManageDomains && (
                        isActive ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeactivateDomain(domain)}
                            disabled={deactivateMutation.isPending}
                          >
                            <PowerOff className="h-3.5 w-3.5 mr-1" />
                            {t('Deactivate')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleActivate(domain)}
                            disabled={activateMutation.isPending}
                          >
                            <Power className="h-3.5 w-3.5 mr-1" />
                            {t('Activate')}
                          </Button>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </QueryPage>

      {settingsDomain && (
        <DomainSettingsDialog
          isOpen={!!settingsDomain}
          onClose={() => setSettingsDomain(null)}
          domain={settingsDomain}
        />
      )}

      <ConfirmDialog
        isOpen={!!deactivateDomain}
        onClose={() => setDeactivateDomain(null)}
        onConfirm={handleDeactivateConfirm}
        title={`${t('Deactivate')} ${deactivateDomain?.name ?? t('Domain')}?`}
        description={t('Deactivating this domain may block active work items and prevent new assignments. Are you sure you want to continue?')}
        confirmText={t('Deactivate')}
        variant="destructive"
        isLoading={deactivateMutation.isPending}
      />
    </div>
  );
}

/** Legacy page wrapper — the /roros/domains route still uses this. */
export function DomainsPage() {
  return (
    <RorosPage
      title={t('Domains')}
      icon={<Globe className="h-5 w-5" />}
    >
      <DomainsPanel />
    </RorosPage>
  );
}
